import { openDayDate } from '../../utils/openDayDate';
/** @jsxImportSource @emotion/react */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useSave } from '../../contexts/WorldContext';
import { useSelector } from 'react-redux';
import {
  Card, Button, Space, Tag, Typography, Empty, Spin, Statistic,
  Row, Col, Select, List,
  Tooltip, Divider, DatePicker, Modal, message,
} from 'antd';
import {
  CalendarOutlined, StepForwardOutlined, ThunderboltOutlined,
  WifiOutlined, DisconnectOutlined,
  HeartOutlined, AlertOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { formatDate, formatList } from '../../i18n';
import MainLayout from '../../components/common/MainLayout';
import {
  useGetSaveRuntimeSessionQuery,
  useAdvanceSaveSimDaysMutation,
  useGetSimulationSessionWeatherQuery,
  useGetSimOverviewQuery,
  useResetSaveSimSessionMutation,
  useMaterializeWeatherYearMutation,
  useListWeatherMapsQuery,
  useGetWeatherImportLocationsQuery,
} from '../../api/simulationApi';
import { useGetPlantingsQuery } from '../../api/plantingsApi';
import { useSimulationWebSocket } from '../../hooks/useSimulationWebSocket';
import { useRequestHalt } from '../../utils/requestHalt';
import { selectIsAuthenticated } from '../../store/slices/authSlice';
import type {
  SimSessionStatus,
  AdvanceResult,
  SimSessionWithAdvance,
  DayOverview,
  WeatherImportProvider,
  WeatherYearSelection,
  WeatherYearSelectionRequest,
} from '../../types/simulation';
import type { Planting } from '../../models/Planting';
import { css } from '@emotion/react';
import EChart from '../../components/charts/EChart';
import WeatherMapPanel from './WeatherMapPanel';
import GreenhouseClimatePanel from './GreenhouseClimatePanel';

const { Text, Title } = Typography;

const STATUS_COLORS: Record<SimSessionStatus, string> = {
  active: 'green',
  paused: 'orange',
  completed: 'blue',
  cancelled: 'default',
};
// Подписи статусов: simulation:page.status.<status>

type AdvanceSaveRequest = { saveId: string; days?: number; targetDate?: string };
type PendingWeatherYear = WeatherYearSelectionRequest & { retryPayload: AdvanceSaveRequest };

const extractWeatherYearRequest = (
  error: unknown,
  retryPayload: AdvanceSaveRequest,
): PendingWeatherYear | null => {
  const response = (error as { data?: unknown })?.data;
  if (!response || typeof response !== 'object') return null;
  const body = response as { errorCode?: string; data?: unknown };
  if (body.errorCode !== 'WEATHER_YEAR_SELECTION_REQUIRED' || !body.data || typeof body.data !== 'object') {
    return null;
  }
  const request = body.data as WeatherYearSelectionRequest;
  if (!request.seriesId || !Number.isInteger(Number(request.targetYear))) return null;
  return { ...request, targetYear: Number(request.targetYear), retryPayload };
};

const styles = {
  timePanel: css({
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    borderRadius: 12,
    padding: '24px 32px',
    color: '#fff',
    marginBottom: 16,
  }),
  simDate: css({
    fontSize: 36,
    fontWeight: 700,
    fontFamily: 'monospace',
    letterSpacing: 2,
  }),
  simDateRow: css({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  }),
  calendarPicker: css({
    width: 48,
    height: 48,
    background: 'rgba(255,255,255,0.08)',
    borderColor: 'rgba(255,255,255,0.3)',
    cursor: 'pointer',
    '&:hover, &.ant-picker-focused': {
      background: 'rgba(255,255,255,0.14)',
      borderColor: 'rgba(255,255,255,0.65)',
    },
    '& .ant-picker-input > input': {
      width: 0,
      minWidth: 0,
      padding: 0,
      cursor: 'pointer',
    },
    '& .ant-picker-suffix': {
      marginInlineStart: 0,
      color: '#fff',
      fontSize: 20,
    },
  }),
  dayCounter: css({
    fontSize: 14,
    opacity: 0.7,
    marginTop: 4,
  }),
  controlBtn: css({
    minWidth: 48,
    minHeight: 48,
  }),
  wsIndicator: css({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    padding: '2px 8px',
    borderRadius: 10,
  }),
};

function normalizeChartNumber(value: unknown, precision = 4): number {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  const factor = 10 ** precision;
  return Math.round((num + Number.EPSILON) * factor) / factor;
}

function formatAxisNumber(value: unknown, precision = 2): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  const normalized = normalizeChartNumber(num, precision + 2);
  if (Math.abs(normalized) < 1e-9) return '0';
  const fixed = normalized.toFixed(precision);
  return String(Number(fixed));
}

// ---- Cause labels ----
// Подписи: simulation:page.causes.<key>, ключ — код причины с ':' → '_'.
const CAUSE_KEYS = new Set([
  'soil:deficit', 'soil:excess', 'temperature:cold', 'temperature:heat', 'climateWater', 'light',
  'disease', 'disease:late_blight', 'disease:fusarium_wilt', 'disease:powdery_mildew',
  'disease:downy_mildew', 'disease:gray_mold', 'disease:root_rot', 'disease:aphids',
  'disease:whitefly', 'disease:spider_mite',
]);
const causeLabel = (t: (key: string) => string, cause: string) =>
  (CAUSE_KEYS.has(cause) ? t(`page.causes.${cause.replace(':', '_')}`) : cause);

const BUCKET_COLORS: Record<string, string> = {
  ok: '#52c41a',
  excellent: '#52c41a',
  mild: '#fadb14',
  good: '#73d13d',
  moderate: '#fa8c16',
  fair: '#faad14',
  severe: '#f5222d',
  poor: '#ff4d4f',
  critical: '#820014',
};

// ---- Overview Panel ----

const OverviewPanel: React.FC<{
  overview: DayOverview;
  plantingLabelById: Map<string, string>;
  bedNameById: Map<string, string>;
}> = ({ overview, bedNameById }) => {
  const { t } = useTranslation('simulation');
  const hb = overview.healthBuckets;
  const sb = overview.stressBuckets;
  const total = overview.totalPlantings;

  const healthSummary = useMemo(() => {
    const parts: string[] = [];
    if (hb.critical > 0) parts.push(t('page.overview.healthCritical', { count: hb.critical }));
    if (hb.poor > 0) parts.push(t('page.overview.healthPoor', { count: hb.poor }));
    if (hb.fair > 0) parts.push(t('page.overview.healthFair', { count: hb.fair }));
    return parts.length > 0 ? formatList(parts, { type: 'unit' }) : t('page.overview.allHealthy');
  }, [hb, t]);

  const stressSummary = useMemo(() => {
    if (!sb) return null;
    const parts: string[] = [];
    if (sb.critical > 0) parts.push(t('page.overview.stressCritical', { count: sb.critical }));
    if (sb.severe > 0) parts.push(t('page.overview.stressSevere', { count: sb.severe }));
    if (sb.moderate > 0) parts.push(t('page.overview.stressModerate', { count: sb.moderate }));
    return parts.length > 0 ? formatList(parts, { type: 'unit' }) : t('page.overview.stressNormal');
  }, [sb, t]);

  const stressedTotal = sb
    ? sb.mild + sb.moderate + sb.severe + sb.critical
    : null;

  return (
    <Card
      size="small"
      title={
        <Space>
          <HeartOutlined />
          <span>{t('page.overview.title')}</span>
          {overview.isColdStart && (
            <Tag color="default">{t('page.overview.fromSavedData')}</Tag>
          )}
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      <Row gutter={[16, 12]}>
        {/* Health */}
        <Col span={6}>
          <Statistic
            title={t('page.overview.health')}
            value={hb.excellent + hb.good}
            suffix={t('page.overview.healthSuffix', { total })}
            valueStyle={{ color: hb.critical + hb.poor > 0 ? '#cf1322' : '#389e0d' }}
            prefix={<HeartOutlined />}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>{healthSummary}</Text>
        </Col>

        {/* Stress (only from fresh advance) */}
        <Col span={6}>
          {sb ? (
            <>
              <Statistic
                title={t('page.overview.stress')}
                value={stressedTotal ?? 0}
                suffix={t('page.overview.stressSuffix', { total })}
                valueStyle={{ color: (stressedTotal ?? 0) > 0 ? '#fa8c16' : '#389e0d' }}
                prefix={<AlertOutlined />}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>{stressSummary}</Text>
            </>
          ) : (
            <Statistic title={t('page.overview.stress')} value="—" prefix={<AlertOutlined />} />
          )}
        </Col>

        {/* Dead */}
        <Col span={4}>
          <Statistic
            title={t('page.overview.dead')}
            value={overview.deadCount}
            valueStyle={{ color: overview.deadCount > 0 ? '#cf1322' : undefined }}
          />
        </Col>

        {/* Dominant causes */}
        <Col span={8}>
          <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
            {t('page.overview.mainCauses')}
          </Text>
          {overview.dominantCauses.length > 0 ? (
            <Space wrap size={4}>
              {overview.dominantCauses.map((c) => (
                <Tag key={c.cause} color={BUCKET_COLORS[c.count > 3 ? 'severe' : 'moderate'] ? 'orange' : 'default'}>
                  {t('page.overview.causeCount', { cause: causeLabel(t, c.cause), count: c.count })}
                </Tag>
              ))}
            </Space>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {overview.isColdStart ? t('page.overview.causesUnavailable') : t('page.overview.noCauses')}
            </Text>
          )}
        </Col>
      </Row>

      {/* Beds at risk */}
      {overview.bedsAtRisk.length > 0 && (
        <>
          <Divider style={{ margin: '8px 0' }} />
          <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
            {t('page.overview.bedsAtRisk')}
          </Text>
          <Space wrap size={4}>
            {overview.bedsAtRisk.map((b) => (
              <Tag
                key={b.bedId}
                color={BUCKET_COLORS[b.bucket] ?? 'default'}
              >
                {bedNameById.get(b.bedId) ?? b.bedId.slice(0, 8)} ({b.plantingsAffected}/{b.totalPlantings})
              </Tag>
            ))}
          </Space>
        </>
      )}
    </Card>
  );
};

// ---- Main Page ----

const SimulationPage: React.FC = () => {
  const save = useSave();
  const farmId = save.farmId;
  if (!farmId) return <Navigate to={`/saves/${save.id}`} replace />;

  return <SimulationPageInner saveId={save.id} farmId={farmId} />;
};

const SimulationPageInner: React.FC<{ saveId: string; farmId: string }> = ({ saveId, farmId }) => {
  const save = useSave();
  const { t } = useTranslation('simulation');
  const { halted } = useRequestHalt();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { data: session, isLoading, refetch } = useGetSaveRuntimeSessionQuery(saveId, {
    skip: halted || !isAuthenticated || !saveId,
  });

  const [advanceDays, { isLoading: advancing }] = useAdvanceSaveSimDaysMutation();
  const [resetSession, { isLoading: resetting }] = useResetSaveSimSessionMutation();
  const [materializeWeatherYear, { isLoading: materializingWeatherYear }] = useMaterializeWeatherYearMutation();

  const { data: plantings } = useGetPlantingsQuery({ saveId, lifecycleStatus: 'active' });

  // Cold-start overview (from CPV) — used when no fresh advance result is available
  const { data: coldStartOverview, refetch: refetchOverview } = useGetSimOverviewQuery(session?.id ?? '', {
    skip: !session?.id,
  });

  const [latestAdvance, setLatestAdvance] = useState<AdvanceResult | null>(null);

  useEffect(() => {
    setLatestAdvance(null);
  }, [session?.id]);

  const [pendingWeatherYear, setPendingWeatherYear] = useState<PendingWeatherYear | null>(null);
  const [weatherYearSelectionType, setWeatherYearSelectionType] =
    useState<WeatherYearSelection['type']>('climatology_average');
  const [historicalWeatherYear, setHistoricalWeatherYear] = useState<number | null>(null);
  const [historicalWeatherProvider, setHistoricalWeatherProvider] =
    useState<WeatherImportProvider>('nasa_power');
  const [sourceWeatherSeriesId, setSourceWeatherSeriesId] = useState<string | null>(null);
  const { data: weatherYearMaps } = useListWeatherMapsQuery(farmId, {
    skip: !pendingWeatherYear,
  });
  const { data: weatherImportLocations } = useGetWeatherImportLocationsQuery(undefined, {
    skip: !pendingWeatherYear,
  });

  const plantingLabelById = useMemo(() => {
    const map = new Map<string, string>();
    (plantings || []).forEach((planting: Planting) => {
      const location = planting.locations?.[0];
      const label = [
        planting.crop?.name,
        location?.bed?.anchor_code || location?.bed?.name,
        location?.plot?.name,
        location?.field?.name,
      ].filter(Boolean).join(' / ');
      map.set(String(planting.id), label);
    });
    return map;
  }, [plantings]);

  const bedNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (!plantings) return map;
    plantings.forEach((p: Planting) => {
      (p.locations || []).forEach((loc) => {
        const bedId = String(loc.bedId);
        if (!map.has(bedId) && loc.bed) {
          map.set(bedId, loc.bed.anchor_code || loc.bed.name || bedId.slice(0, 8));
        }
      });
    });
    return map;
  }, [plantings]);

  const clearAdvanceHistory = useCallback(() => {
    setLatestAdvance(null);
  }, []);

  const consumeAdvanceResult = useCallback((result: SimSessionWithAdvance) => {
    if (result.advanceResult) {
      setLatestAdvance(result.advanceResult);
      return;
    }

    const days = result.multiDaySummary?.days;
    if (!days?.length) return;
    const ordered = [...days].sort((a, b) => a.toDate.localeCompare(b.toDate));
    const first = ordered[0];
    const last = ordered[ordered.length - 1];
    const alertsDetails = ordered.flatMap((entry) => entry.alertsDetails || []);
    setLatestAdvance({
      ...last,
      fromDate: first.fromDate,
      daysAdvanced: result.multiDaySummary?.daysAdvanced || ordered.length,
      deltasCount: result.multiDaySummary?.totalDeltas
        ?? ordered.reduce((sum, entry) => sum + entry.deltasCount, 0),
      alerts: result.multiDaySummary?.totalAlerts
        ?? ordered.reduce((sum, entry) => sum + entry.alerts, 0),
      alertsDetails,
      deltas: [],
    });
  }, []);

  const handleAdvanceFailure = useCallback((error: unknown, payload: AdvanceSaveRequest) => {
    const request = extractWeatherYearRequest(error, payload);
    if (!request) return false;
    setPendingWeatherYear(request);
    setWeatherYearSelectionType(request.defaultSelection?.type || 'climatology_average');
    const recentYear = weatherImportLocations?.recentCompleteYears?.[0] || new Date().getUTCFullYear() - 1;
    setHistoricalWeatherYear(recentYear);
    setHistoricalWeatherProvider('nasa_power');
    setSourceWeatherSeriesId(null);
    return true;
  }, [weatherImportLocations?.recentCompleteYears]);

  const handleMaterializeWeatherYear = useCallback(async () => {
    if (!pendingWeatherYear) return;
    let selection: WeatherYearSelection;
    if (weatherYearSelectionType === 'historical_year') {
      if (!historicalWeatherYear) {
        message.error(t('page.weatherYear.selectHistoricalYear'));
        return;
      }
      selection = {
        type: 'historical_year',
        sourceYear: historicalWeatherYear,
        provider: historicalWeatherProvider,
      };
    } else if (weatherYearSelectionType === 'weather_map') {
      if (!sourceWeatherSeriesId) {
        message.error(t('page.weatherYear.selectWeatherMap'));
        return;
      }
      selection = { type: 'weather_map', sourceSeriesId: sourceWeatherSeriesId };
    } else {
      selection = { type: 'climatology_average' };
    }

    try {
      await materializeWeatherYear({
        seriesId: pendingWeatherYear.seriesId,
        targetYear: pendingWeatherYear.targetYear,
        selection,
      }).unwrap();
      const retryPayload = pendingWeatherYear.retryPayload;
      setPendingWeatherYear(null);
      const result = await advanceDays(retryPayload).unwrap() as SimSessionWithAdvance;
      consumeAdvanceResult(result);
      refetch();
    } catch (error) {
      const data = (error as { data?: { message?: string } })?.data;
      message.error(data?.message || t('page.weatherYear.prepareFailed'));
    }
  }, [
    advanceDays,
    consumeAdvanceResult,
    historicalWeatherProvider,
    historicalWeatherYear,
    materializeWeatherYear,
    pendingWeatherYear,
    refetch,
    sourceWeatherSeriesId,
    t,
    weatherYearSelectionType,
  ]);

  // WebSocket for real-time updates
  const { connected, lastAdvance } = useSimulationWebSocket({
    sessionId: session?.id ?? null,
    onAdvanced: useCallback((advance: AdvanceResult) => {
      setLatestAdvance(advance);
    }, []),
    onReset: useCallback(() => {
      clearAdvanceHistory();
      void Promise.all([refetch(), refetchOverview()]);
    }, [clearAdvanceHistory, refetch, refetchOverview]),
    onStatusChange: useCallback(() => {
      refetch();
    }, [refetch]),
  });

  const handleAdvance = useCallback(async () => {
    if (!session) return;
    const payload = { saveId, days: 1 };
    try {
      const result = await advanceDays(payload).unwrap() as SimSessionWithAdvance;
      consumeAdvanceResult(result);
      refetch();
    } catch (error) {
      handleAdvanceFailure(error, payload);
    }
  }, [saveId, session, advanceDays, refetch, consumeAdvanceResult, handleAdvanceFailure]);

  // Jump to specific date (forward via advance, backward via reset)
  const handleJumpToDate = useCallback(async (targetDate: dayjs.Dayjs | null) => {
    if (!session || !targetDate) return;
    const current = dayjs(openDayDate(session.simDate, save.type));
    const diff = targetDate.diff(current, 'day');
    if (diff === 0) return;
    const targetDateStr = targetDate.subtract(save.type === 'free' ? 1 : 0, 'day').format('YYYY-MM-DD');

    if (diff > 0) {
      try {
        const payload = {
          saveId,
          targetDate: targetDateStr,
        };
        const result = await advanceDays(payload).unwrap() as SimSessionWithAdvance;
        consumeAdvanceResult(result);
        refetch();
      } catch (error) {
        handleAdvanceFailure(error, { saveId, targetDate: targetDateStr });
      }
    } else {
      // Откат удаляет будущее, кроме посадок и графиков теплиц (rewindPolicy
      // на сервере) — без подтверждения не выполняем.
      const dateLabel = formatDate(targetDateStr, 'medium');
      Modal.confirm({
        title: t('common:messages.rewindTitle', { date: dateLabel }),
        content: t('common:messages.rewindRemoves', { date: dateLabel }),
        okText: t('common:actions.confirm'),
        okButtonProps: { danger: true },
        cancelText: t('common:actions.cancel'),
        onOk: async () => {
          try {
            await resetSession({ saveId, targetDate: targetDateStr }).unwrap();
            clearAdvanceHistory();
            await Promise.all([refetch(), refetchOverview()]);
          } catch {
            // error handled by RTK
          }
        },
      });
    }
  }, [saveId, save.type, session, advanceDays, resetSession, refetch, refetchOverview, consumeAdvanceResult, handleAdvanceFailure, clearAdvanceHistory, t]);

  const historicalYearOptions = useMemo(() => {
    const fallback = new Date().getUTCFullYear() - 1;
    const years = weatherImportLocations?.recentCompleteYears?.length
      ? weatherImportLocations.recentCompleteYears
      : [fallback, fallback - 1, fallback - 2, fallback - 3, fallback - 4];
    return years.map((year) => ({ value: year, label: String(year) }));
  }, [weatherImportLocations?.recentCompleteYears]);

  const weatherMapOptions = useMemo(
    () => (weatherYearMaps || [])
      .filter((map) => map.id !== pendingWeatherYear?.seriesId)
      .map((map) => ({
        value: map.id,
        label: map.isSystem ? t('page.weatherYear.systemMap', { name: map.name }) : map.name,
      })),
    [pendingWeatherYear?.seriesId, weatherYearMaps, t],
  );

  const busy = advancing || resetting || materializingWeatherYear;
  const isActive = session?.status === 'active';

  // Latest advance summary
  const latestCandidate = latestAdvance || lastAdvance;
  const latest = latestCandidate && session?.simDate
    && dayjs(latestCandidate.toDate).isSame(dayjs(session.simDate), 'day')
    ? latestCandidate
    : null;
  const latestAlertDetails = latest?.alertsDetails || [];

  // Overview: prefer fresh advance overview, fall back to cold-start from CPV
  const activeOverview: DayOverview | null = latest?.overview ?? coldStartOverview ?? null;

  const weatherFrom = useMemo(() => {
    if (!session?.simDate) return undefined;
    const endDate = dayjs(session.simDate);
    return endDate.subtract(30, 'day').format('YYYY-MM-DD');
  }, [session?.simDate]);

  const weatherTo = useMemo(() => {
    if (!session?.simDate) return undefined;
    return dayjs(session.simDate).format('YYYY-MM-DD');
  }, [session?.simDate]);

  const { data: simulatedWeather, isFetching: loadingSimulatedWeather } = useGetSimulationSessionWeatherQuery(
    {
      sessionId: session?.id || '',
      from: weatherFrom,
      to: weatherTo,
    },
    {
      skip: !session?.id,
      refetchOnFocus: true,
    }
  );

  const { data: simulatedWeatherFallback, isFetching: loadingSimulatedWeatherFallback } = useGetSimulationSessionWeatherQuery(
    {
      sessionId: session?.id || '',
    },
    {
      skip: !session?.id || !!simulatedWeather?.points?.length,
      refetchOnFocus: true,
    }
  );

  const weatherChartData = (simulatedWeather?.points?.length ? simulatedWeather : simulatedWeatherFallback);
  const loadingWeatherCharts = loadingSimulatedWeather || loadingSimulatedWeatherFallback;

  const temperatureChartOption = useMemo<any>(() => {
    const points = weatherChartData?.points || [];
    return {
      tooltip: {
        trigger: 'axis',
        valueFormatter: (value: unknown) => formatAxisNumber(value, 2),
      },
      legend: { data: ['Tmin', 'Tavg', 'Tmax'] },
      xAxis: { type: 'category', data: points.map((p) => p.date) },
      yAxis: {
        type: 'value',
        name: '°C',
        scale: true,
        axisLabel: {
          formatter: (value: number) => formatAxisNumber(value, 2),
        },
      },
      dataZoom: [
        { type: 'inside' },
        { type: 'slider', start: 70, end: 100 },
      ],
      series: [
        {
          name: 'Tmin',
          type: 'line',
          smooth: true,
          data: points.map((p) => normalizeChartNumber(p.tempMin ?? 0)),
        },
        {
          name: 'Tavg',
          type: 'line',
          smooth: true,
          data: points.map((p) => normalizeChartNumber(p.tempAvg ?? 0)),
        },
        {
          name: 'Tmax',
          type: 'line',
          smooth: true,
          data: points.map((p) => normalizeChartNumber(p.tempMax ?? 0)),
        },
      ],
    };
  }, [weatherChartData]);

  const factorsChartOption = useMemo<any>(() => {
    const points = weatherChartData?.points || [];
    const humidityName = t('page.charts.humidity');
    const precipitationName = t('page.charts.precipitation');
    const sunName = t('page.charts.sun');
    return {
      tooltip: {
        trigger: 'axis',
        valueFormatter: (value: unknown) => formatAxisNumber(value, 2),
      },
      legend: { data: [humidityName, precipitationName, sunName] },
      xAxis: { type: 'category', data: points.map((p) => p.date) },
      yAxis: [
        {
          type: 'value',
          name: '%',
          axisLabel: {
            formatter: (value: number) => formatAxisNumber(value, 2),
          },
        },
        {
          type: 'value',
          name: t('page.charts.mmPerHour'),
          scale: true,
          axisLabel: {
            formatter: (value: number) => formatAxisNumber(value, 2),
          },
        },
      ],
      dataZoom: [
        { type: 'inside' },
        { type: 'slider', start: 70, end: 100 },
      ],
      series: [
        {
          name: humidityName,
          type: 'line',
          smooth: true,
          data: points.map((p) => normalizeChartNumber(p.humidityPct ?? 0)),
        },
        {
          name: precipitationName,
          type: 'line',
          smooth: true,
          yAxisIndex: 1,
          data: points.map((p) => normalizeChartNumber(p.precipitationMm ?? 0)),
        },
        {
          name: sunName,
          type: 'line',
          smooth: true,
          yAxisIndex: 1,
          data: points.map((p) => normalizeChartNumber(p.outdoorSunHours ?? 0)),
        },
      ],
    };
  }, [weatherChartData, t]);

  return (
    <MainLayout>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Space align="center" style={{ marginBottom: 16 }}>
          <ThunderboltOutlined style={{ fontSize: 24 }} />
          <Title level={3} style={{ margin: 0 }}>{t('page.title')}</Title>
          {session && (
            <Tag color={STATUS_COLORS[session.status]}>{t(`page.status.${session.status}`, { defaultValue: session.status })}</Tag>
          )}
          <div css={styles.wsIndicator}>
            {connected ? (
              <><WifiOutlined style={{ color: '#52c41a' }} /> WS</>
            ) : (
              <><DisconnectOutlined style={{ color: '#999' }} /> offline</>
            )}
          </div>
        </Space>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin size="large" />
          </div>
        ) : !session ? (
          <Card>
            <Empty
              description={t('page.sessionLoadFailed')}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={() => refetch()}
                size="large"
              >
                {t('page.retryLoad')}
              </Button>
            </Empty>
          </Card>
        ) : (
          <>
            {/* Time Display Panel */}
            <div css={styles.timePanel}>
              <Row align="middle" gutter={24}>
                <Col flex="auto">
                  <div css={styles.simDateRow}>
                    <div css={styles.simDate}>{openDayDate(session.simDate, save.type)}</div>
                    <Tooltip title={t('page.jumpToDate')}>
                      <DatePicker
                        css={styles.calendarPicker}
                        value={null}
                        allowClear={false}
                        inputReadOnly
                        placeholder=""
                        suffixIcon={<CalendarOutlined />}
                        disabled={!isActive || busy}
                        // Open the panel ON the sim date, NOT real "today": the picker
                        // is a SIM-timeline control, so its default month/year must be
                        // the simulation's — otherwise picking "May" lands on the
                        // real-calendar year (off-by-a-year cursor desync).
                        defaultPickerValue={session.simDate ? dayjs(openDayDate(session.simDate, save.type)) : undefined}
                        disabledDate={(date) => date.isSame(dayjs(openDayDate(session.simDate, save.type)), 'day')}
                        onChange={handleJumpToDate}
                      />
                    </Tooltip>
                  </div>
                  <div css={styles.dayCounter}>{save.type === 'free' ? t('page.todayOpenDay') : t('page.scenarioDate')}</div>
                </Col>
                <Col>
                  {isActive && (
                    <Tooltip title={t('corner.advanceDay')}>
                      <Button
                        css={styles.controlBtn}
                        icon={<StepForwardOutlined />}
                        onClick={handleAdvance}
                        loading={advancing}
                        disabled={busy}
                        type="primary"
                        size="large"
                      />
                    </Tooltip>
                  )}
                </Col>
              </Row>
            </div>

            {/* Farm Overview Panel */}
            {activeOverview && activeOverview.totalPlantings > 0 && (
              <OverviewPanel overview={activeOverview} plantingLabelById={plantingLabelById} bedNameById={bedNameById} />
            )}

            <Card
              title={t('page.charts.title')}
              size="small"
              style={{ marginBottom: 16 }}
            >
              {loadingWeatherCharts ? (
                <Spin />
              ) : !(weatherChartData?.points?.length) ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('page.charts.noData')} />
              ) : (
                <Row gutter={16}>
                  <Col span={12}>
                    <Card size="small" title={t('page.charts.temperature')}>
                      <EChart option={temperatureChartOption} height={280} />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title={t('page.charts.weatherFactors')}>
                      <EChart option={factorsChartOption} height={280} />
                    </Card>
                  </Col>
                </Row>
              )}
            </Card>

            {/* Weather Map Editor */}
            <WeatherMapPanel
              saveId={saveId}
              farmId={farmId}
            />

            <GreenhouseClimatePanel
              farmId={farmId}
              sessionId={session.id}
              saveId={session.saveId || null}
              simDate={session.simDate}
            />

            <Card
              title={t('page.events.title')}
              size="small"
              extra={latest ? (
                <Text type="secondary">{latest.fromDate} → {latest.toDate}</Text>
              ) : null}
            >
              {(latestAlertDetails.length === 0) ? (
                (latest?.alerts ?? 0) > 0 ? (
                  <Text type="warning">{t('page.events.noDetails')}</Text>
                ) : latest ? (
                  <Text type="secondary">{t('page.events.none')}</Text>
                ) : (
                  <Text type="secondary">{t('page.events.hint')}</Text>
                )
              ) : (
                <List
                  size="small"
                  dataSource={latestAlertDetails}
                  pagination={latestAlertDetails.length > 10 ? { pageSize: 10, size: 'small' } : false}
                  renderItem={(item) => (
                    <List.Item>
                      <Space direction="vertical" size={2}>
                        <Space>
                          <Tag color={item.type === 'death' ? 'red' : 'orange'}>{item.type}</Tag>
                          <Text type="secondary">{item.date}</Text>
                          <Link to={`/saves/${saveId}/plantings/${item.plantingId}`}>
                            {plantingLabelById.get(String(item.plantingId)) || t('page.events.plantingFallback', { id: item.plantingId.slice(0, 8) })}
                          </Link>
                        </Space>
                        <Text>{item.message}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </>
        )}

        <Modal
          open={Boolean(pendingWeatherYear)}
          title={pendingWeatherYear
            ? t('page.weatherYear.titleForYear', { year: pendingWeatherYear.targetYear })
            : t('page.weatherYear.title')}
          okText={t('page.weatherYear.ok')}
          cancelText={t('common:actions.cancel')}
          confirmLoading={materializingWeatherYear || advancing}
          onOk={handleMaterializeWeatherYear}
          onCancel={() => setPendingWeatherYear(null)}
          destroyOnClose
        >
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Text>
              {t('page.weatherYear.intro')}
            </Text>
            <Select
              value={weatherYearSelectionType}
              style={{ width: '100%' }}
              onChange={(value: WeatherYearSelection['type']) => setWeatherYearSelectionType(value)}
              options={[
                { value: 'climatology_average', label: t('page.weatherYear.climatologyAverage') },
                { value: 'historical_year', label: t('page.weatherYear.historicalYear') },
                { value: 'weather_map', label: t('page.weatherYear.otherMap') },
              ]}
            />
            {weatherYearSelectionType === 'historical_year' && (
              <Space style={{ width: '100%' }}>
                <Select
                  value={historicalWeatherYear}
                  style={{ width: 150 }}
                  placeholder={t('page.weatherYear.year')}
                  options={historicalYearOptions}
                  onChange={setHistoricalWeatherYear}
                />
                <Select
                  value={historicalWeatherProvider}
                  style={{ width: 180 }}
                  onChange={setHistoricalWeatherProvider}
                  options={[
                    { value: 'nasa_power', label: 'NASA POWER' },
                    { value: 'open_meteo', label: 'Open-Meteo' },
                  ]}
                />
              </Space>
            )}
            {weatherYearSelectionType === 'weather_map' && (
              <Select
                showSearch
                value={sourceWeatherSeriesId}
                style={{ width: '100%' }}
                placeholder={t('page.weatherYear.selectWeatherMap')}
                optionFilterProp="label"
                options={weatherMapOptions}
                onChange={setSourceWeatherSeriesId}
              />
            )}
            <Text type="secondary">
              {t('page.weatherYear.changeWarning')}
            </Text>
          </Space>
        </Modal>

      </div>
    </MainLayout>
  );
};

export default SimulationPage;
